import { useMemoizedFn } from 'ahooks';
import { Calendar, CalendarProps } from 'antd';
import classNames from 'classnames';
import { PlayLeftIcon, PlayRightIcon } from 'components/Icons';
import { RevxDate } from 'components/RevxDate';
import {
  JobConfigShippingDate,
  JobShippingSpec,
} from 'domains/jobconfigDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { minBy } from 'lodash-es';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { isSameUTCDay, toLocaleString } from 'utils/misc';
import './index.less';
import { PriceNumber } from './PriceNumber';

export interface RevxCalendarProps extends CalendarProps<Moment> {
  title?: number | string;
  datePriceItems?: JobConfigShippingDate[];
  color: 'yellow' | 'purple' | 'green';
  enablePrice?: boolean;
  svcCache?: { [index: string]: string };
  jobShippingSpec?: JobShippingSpec;
}
export const RevxJobConfigCalendar: React.FC<RevxCalendarProps> = ({
  title,
  datePriceItems,
  defaultValue,
  value,
  color,
  enablePrice,
  onChange,
  svcCache,
  jobShippingSpec,
  ...props
}) => {
  const [curCalValue, setCurCalValue] = useState(moment(value || defaultValue));
  const [calendarValue, setCalendarValue] = useState(
    moment(value || defaultValue)
  );

  useEffect(() => {
    if (value) {
      setCurCalValue(moment(value));
      setCalendarValue(moment(value));
    }
  }, [value]);

  const getPriceItemDate = useMemoizedFn((value) => {
    const hit = (datePriceItems || []).find((item) => {
      return isSameUTCDay(moment(item.calendarDate), value);
    });

    return hit;
  });

  return (
    <div
      className={classNames('job-config-cal', color, {
        'enable-price': enablePrice,
      })}
    >
      {enablePrice && title && (
        <div className="job-config-cal-title">{toLocaleString(title)}</div>
      )}

      <Calendar
        fullscreen={false}
        value={calendarValue}
        {...props}
        headerRender={({ value, type, onChange }) => {
          return (
            <div className="job-config-cal-header">
              <div
                onClick={() => {
                  let newDate = value.clone().subtract(1, 'M');

                  // if (
                  //   props.disabledDate &&
                  //   props.disabledDate(newDate.clone().endOf('month'))
                  // ) {
                  //   return;
                  // }

                  // if (props.disabledDate && props.disabledDate(newDate)) {
                  //   newDate = newDate.endOf('month');
                  // }

                  setCalendarValue(newDate);
                }}
              >
                <span
                  className={classNames('cal-icon', {
                    'cal-icon-disabled':
                      props.disabledDate &&
                      props.disabledDate(
                        value.clone().subtract(1, 'M').endOf('month')
                      ),
                  })}
                >
                  <PlayLeftIcon className="job-config-cal-prev-month" />
                </span>
              </div>
              <div className="job-config-cal-cur-month">
                <RevxDate dateStr={value} formatStr="MMMM YYYY" />
              </div>
              <div
                onClick={() => {
                  let newDate = value.clone().add(1, 'M');

                  // if (
                  //   props.disabledDate &&
                  //   props.disabledDate(newDate.clone().startOf('month'))
                  // ) {
                  //   return;
                  // }

                  // if (props.disabledDate && props.disabledDate(newDate)) {
                  //   newDate = newDate.startOf('month');
                  // }

                  setCalendarValue(newDate);
                }}
              >
                <span
                  className={classNames('cal-icon', {
                    'cal-icon-disabled':
                      props.disabledDate &&
                      props.disabledDate(
                        value.clone().add(1, 'M').startOf('month')
                      ),
                  })}
                >
                  <PlayRightIcon className="job-config-cal-next-month" />
                </span>
              </div>
            </div>
          );
        }}
        disabledDate={(value: Moment) => {
          if (enablePrice) {
            return !getPriceItemDate(value);
          } else {
            if (props.disabledDate) {
              return props.disabledDate(value);
            }
            return false;
          }
        }}
        dateFullCellRender={(value) => {
          const priceItemDate = getPriceItemDate(value);
          return (
            <div
              className={classNames('job-config-cal-cell', {
                highlight: enablePrice
                  ? !!priceItemDate &&
                    curCalValue &&
                    isSameUTCDay(value, curCalValue)
                  : curCalValue && isSameUTCDay(value, curCalValue),
                'active-item': !!priceItemDate,
              })}
            >
              <span
                className={
                  !priceItemDate?.services.find((sItem) => !sItem.rush)
                    ? `job-config-cal-cell-rush`
                    : `job-config-cal-cell-date`
                }
              >
                <RevxDate dateStr={value} formatStr="D" />{' '}
              </span>

              {!!priceItemDate && priceItemDate?.services?.length > 0 && (
                <>
                  {!priceItemDate?.services.find((sItem) => !sItem.rush) ? (
                    <span className="job-config-cal-cell-price">RUSH</span>
                  ) : (
                    <>
                      {priceItemDate?.services.find(
                        (sItem) =>
                          !sItem.rush &&
                          sItem.carrierType !== CarrierTypeEnum.Nonstandard
                      )?.shippingTotalCharge ||
                      priceItemDate?.services.find(
                        (sItem) =>
                          !sItem.rush &&
                          sItem.carrierType !== CarrierTypeEnum.Nonstandard
                      )?.freeShipping ? (
                        <PriceNumber
                          price={
                            (
                              priceItemDate.services.find(
                                (item) => item.selected
                              ) ||
                              minBy(
                                priceItemDate?.services.filter(
                                  (sItem) =>
                                    !sItem.rush &&
                                    sItem.carrierType !==
                                      CarrierTypeEnum.Nonstandard
                                ),
                                (item) => item.shippingCost
                              )
                            )?.shippingTotalCharge
                          }
                        />
                      ) : (
                        <>
                          {priceItemDate?.services.find(
                            (sItem) => !sItem.rush && !sItem.tbd
                          )?.shippingTotalCharge ||
                          priceItemDate?.services.find(
                            (sItem) => !sItem.rush && !sItem.tbd
                          )?.freeShipping ||
                          jobShippingSpec?.fulfillment
                            .revshopShippingMarkupPct === -100 ? (
                            <PriceNumber
                              price={
                                (
                                  priceItemDate.services.find(
                                    (item) => item.selected
                                  ) ||
                                  minBy(
                                    priceItemDate?.services.filter(
                                      (sItem) => !sItem.rush && !sItem.tbd
                                    ),
                                    (item) => item.shippingCost
                                  )
                                )?.shippingTotalCharge
                              }
                            />
                          ) : (
                            <span className="job-config-cal-cell-price">
                              QUOTE
                            </span>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          );
        }}
        onChange={(newVal) => {
          setCurCalValue(newVal);
          setCalendarValue(newVal);
          if (enablePrice) {
            if (getPriceItemDate(newVal)) {
              if (onChange) {
                onChange(newVal);
              }
            }
          } else {
            if (onChange) {
              if (!(props.disabledDate && props.disabledDate(newVal))) {
                onChange(newVal);
              }
            }
          }
        }}
      ></Calendar>
    </div>
  );
};
